import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import {
  DropdownButton,
  DropdownMenuItem,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'

import { FILTER_TYPE } from './Medications'

type DateFilterOption = 'last3months' | 'last6months' | 'last12months' | 'anytime'

export interface DateRange {
  start: Date
  end: Date
}

interface DateFilterDropdownProps {
  activeFilter: FILTER_TYPE | null
  defaultValue?: DateFilterOption
  onFilterActive: React.Dispatch<React.SetStateAction<FILTER_TYPE>>
  onSelectionChange: (selection: DateRange | null) => void
  testID?: string
}

export const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({
  onSelectionChange,
  testID = 'DateFilterDropdown',
  defaultValue = 'last3months',
  onFilterActive = noop,
  activeFilter,
}) => {
  const { colors, spacing } = useTheme()
  const intl = useIntl()
  const [selectedValue, setSelectedValue] = useState<DateFilterOption>(defaultValue)

  const dateFilterOptions: Array<{ value: DateFilterOption; label: string }> = [
    {
      value: 'last3months',
      label: intl.formatMessage({
        defaultMessage: 'Last 3 months',
        description: 'Date filter option for last 3 months',
      }),
    },
    {
      value: 'last6months',
      label: intl.formatMessage({
        defaultMessage: 'Last 6 months',
        description: 'Date filter option for last 6 months',
      }),
    },
    {
      value: 'last12months',
      label: intl.formatMessage({
        defaultMessage: 'Last 12 months',
        description: 'Date filter option for last 12 months',
      }),
    },
    {
      value: 'anytime',
      label: intl.formatMessage({
        defaultMessage: 'Anytime',
        description: 'Date filter option for anytime',
      }),
    },
  ]

  const calculateDateRange = (option: DateFilterOption): DateRange | null => {
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + 1)
    switch (option) {
      case 'last3months': {
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 3)
        return { start: startDate, end: endDate }
      }
      case 'last6months': {
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 6)
        return { start: startDate, end: endDate }
      }
      case 'last12months': {
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        return { start: startDate, end: endDate }
      }
      case 'anytime':
        return null
      default:
        return null
    }
  }

  const handleSelection = (value: DateFilterOption) => {
    setSelectedValue(value)
    onSelectionChange(calculateDateRange(value))
  }

  const dropdownItems: DropdownMenuItem[] = dateFilterOptions.map((option) => ({
    text: option.label,
    id: option.value,
    selectHandler: () => handleSelection(option.value),
  }))

  const selectedOption = dateFilterOptions.find((option) => option.value === selectedValue)
  const preselectedValue = selectedOption?.label || dateFilterOptions[0].label

  return (
    <DropdownButton
      style={{
        paddingRight: spacing['16px'],
        paddingLeft: spacing['16px'],
        paddingTop: spacing['12px'],
        paddingBottom: spacing['12px'],
        borderRadius: 8,
        backgroundColor: colors.backgroundPrimary,
        marginTop: spacing['4px'],
        borderColor: activeFilter === FILTER_TYPE.DATE_RANGE ? colors.borderFocus : colors.borderDefault,
        borderWidth: 1,
        height: 48,
      }}
      testID={testID}
      dropdownItems={dropdownItems}
      preselectedValue={preselectedValue}
      dropdownWidth='200px'
      onPress={() => onFilterActive()}
    />
  )
}

export default DateFilterDropdown
